/**
 * Desktop-optimized group stage with split-panel layout.
 *
 * Features:
 * - Left panel: Draggable country list with compact cards
 * - Right panel: Full country details (map, carousel, description)
 * - Unique SVG selection frame connecting both panels
 * - Smooth animations and transitions
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, GripVertical } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { CountryDetails } from '@/components/CountryDetails';
import { CountryLocator } from '@/components/CountryLocator';
import { getCountryByCode } from '@/data/countries';
import type { CountryCode } from '@/data/countries/types';
import { useCountryDisplayData } from '@/hooks/useCountryDisplayData';
import { cn } from '@/lib/utils';
import { CountryImageCarousel } from './CountryImageCarousel';
import { CountryLabel } from './CountryLabel';

// Top 2 countries in each group advance to bracket stage
const QUALIFIERS_PER_GROUP = 2;

interface RatedCountry {
  code: CountryCode;
  stars: number | null;
}

interface GroupStageDesktopProps {
  ratedCountries: RatedCountry[];
  onReorder: (countries: RatedCountry[]) => void;
  disabled?: boolean;
}

/**
 * Sortable country item in the list
 */
function SortableCountryItem({
  country,
  index,
  isSelected,
  onSelect,
  disabled,
}: {
  country: RatedCountry;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  disabled?: boolean;
}) {
  const countryData = getCountryByCode(country.code);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: country.code, disabled });

  // Use CSS.Translate instead of CSS.Transform for smoother animations
  // This avoids scale transforms that can cause jerkiness
  const style = {
    transform: CSS.Translate.toString(transform),
    transition: transition ?? 'transform 200ms ease',
    zIndex: isDragging ? 50 : isSelected ? 10 : 0,
  };

  if (!countryData) return null;

  const qualifies = index < QUALIFIERS_PER_GROUP;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative flex items-stretch rounded-xl overflow-hidden',
        'border-2 cursor-pointer select-none transition-colors',
        isSelected
          ? 'border-primary/60 shadow-lg'
          : 'border-border/60 hover:shadow-md hover:border-border',
        qualifies
          ? 'bg-green-50 dark:bg-green-950/30'
          : 'bg-card opacity-60',
        isDragging && 'opacity-80 shadow-2xl',
      )}
      onClick={onSelect}
    >
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className={cn(
          'flex items-center justify-center w-10 cursor-grab active:cursor-grabbing',
          'bg-muted/30 hover:bg-muted/50 transition-colors',
          'touch-none',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4 text-muted-foreground/60" />
      </div>

      {/* Rank number with qualifying indicator */}
      <div
        className={cn(
          'flex items-center justify-center gap-0.5 min-w-8 px-1.5 text-sm font-medium',
          qualifies ? 'text-green-600' : 'text-muted-foreground',
        )}
      >
        {index + 1}
        {qualifies && <Check className="w-3.5 h-3.5" strokeWidth={2.5} />}
      </div>

      {/* Country info */}
      <div className="flex-1 flex items-center py-3 pr-3 min-w-0">
        <CountryLabel code={country.code} size="md" variant="muted" />
      </div>
    </div>
  );
}

/**
 * Country detail panel showing full information
 */
function CountryDetailPanel({ code }: { code: CountryCode }) {
  const displayData = useCountryDisplayData(code);
  const { country, images } = displayData;

  if (!country) return null;

  return (
    <motion.div
      key={code}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="h-full flex flex-col"
    >
      {/* Header with flag and name */}
      <div className="shrink-0 mb-4">
        <CountryLabel code={code} size="xl" variant="default" showRegionIcon />
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto min-h-0 space-y-4 pr-1">
        {/* Map - taller on larger screens */}
        <div className="h-[200px] md:h-[240px] lg:h-[280px] rounded-xl overflow-hidden border border-border/50 shadow-sm">
          <CountryLocator countryName={country.name} countryCode={code} />
        </div>

        {/* Image Carousel */}
        {images.length > 0 && (
          <div className="rounded-xl overflow-hidden">
            <CountryImageCarousel images={images} countryName={country.name} />
          </div>
        )}

        {/* Details */}
        <div className="bg-card rounded-xl p-4 border border-border/50 shadow-sm">
          <CountryDetails data={displayData} />
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Desktop group stage with split-panel layout
 */
export function GroupStageDesktop({
  ratedCountries,
  onReorder,
  disabled = false,
}: GroupStageDesktopProps) {
  const [selectedCode, setSelectedCode] = useState<CountryCode | null>(
    ratedCountries[0]?.code ?? null,
  );

  // Keep selection valid when countries change
  useEffect(() => {
    if (ratedCountries.length > 0) {
      const isSelectedValid = ratedCountries.some(
        (c) => c.code === selectedCode,
      );
      if (!isSelectedValid) {
        setSelectedCode(ratedCountries[0].code);
      }
    }
  }, [ratedCountries, selectedCode]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = ratedCountries.findIndex((c) => c.code === active.id);
        const newIndex = ratedCountries.findIndex((c) => c.code === over.id);
        const newOrder = arrayMove(ratedCountries, oldIndex, newIndex);
        onReorder(newOrder);
      }
    },
    [ratedCountries, onReorder],
  );

  return (
    <div className="h-full flex gap-6 relative">
      {/* Left panel: Country list */}
      <div className="shrink-0 flex flex-col min-h-0">
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3 px-1">
          Drag to reorder
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={ratedCountries.map((c) => c.code)}
            strategy={verticalListSortingStrategy}
          >
            <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-1">
              {ratedCountries.map((country, index) => (
                <SortableCountryItem
                  key={country.code}
                  country={country}
                  index={index}
                  isSelected={selectedCode === country.code}
                  onSelect={() => setSelectedCode(country.code)}
                  disabled={disabled}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Right panel: Country details */}
      <div className="flex-1 min-w-0 min-h-0">
        <AnimatePresence mode="wait">
          {selectedCode && <CountryDetailPanel code={selectedCode} />}
        </AnimatePresence>
      </div>
    </div>
  );
}
